import { Modal, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import placeholder from "../../../../assets/images/placeholder.png";
import { syncJanToVeko } from "../../../../api/vehicles";
import { fetchVehicleDetails } from "../../../../api/janjapan";
import formatCarName from "../../../../utils/formatCarName";
import convertToNumber from "../../../../utils/convertToNumber";
import extractJanUrl from "../../../../utils/extractJanUrl";
import { statusColors } from "../../../../utils/statusColors";
import { GoSync } from "react-icons/go";

export default function SyncModal({ id, isVisible, setVisible }: any) {
  const queryClient = useQueryClient();

  const { data: car, isLoading: fetching }: any = useQuery({
    queryKey: ["car", id],
    queryFn: () => fetchVehicleDetails({ country_id: 10, car_id: id }),
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: syncJanToVeko,
    onError: (error) => {
      message.error(`${error}`);
    },
    onSuccess: () => {
      message.success("Updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["cars", car?.carId] });
      setVisible(false);
    },
  });

  const onSubmit = async () => {
    if (!car) {
      console.error("Car object is undefined.");
      return;
    }

    if (!Array.isArray(car?.data?.carImages)) {
      console.warn(
        "car.carImages is not an array or is undefined:",
        car.carImages
      );
      return;
    }

    const vimages = car?.data?.carImages
      ?.map((image: any, index: number) => {
        if (!image || !image.imagePath) {
          console.warn(`Image at index ${index} is invalid:`, image);
          return null;
        }

        let category = "others";
        let position = `o${index + 1}`;

        if (index >= 0 && index <= 3) {
          category = "external";
          position = `e${index + 1}`;
        } else if (index >= 4 && index <= 7) {
          category = "internal";
          position = `i${index - 3}`;
        }

        return {
          url: extractJanUrl(image?.imagePath),
          isMain: index === 0,
          category,
          position,
        };
      })
      .filter(Boolean);

    if (vimages.length === 0) {
      console.warn("No valid images in car.carImages.");
      return;
    }
    let state = "Stock In Japan";

    switch (car?.data?.cityName) {
      case "New Loading Invoiced":
        state = "New Loading";
        break;
      case "New Loading":
        state = "New Loading";
        break;
      case "DSM Ali Hassan Mwinyi Road":
        state = "Dar es salaam";
        break;
      case "Mwanza Branch":
        state = "Mwanza";
        break;

      default:
        break;
    }

    const data = {
      name: formatCarName(
        car?.data?.makerName || "",
        car?.data?.modelName || ""
      ),
      make: car?.data?.makerName || "",
      model: car?.data?.modelName || "",
      chassis: car?.data?.chassisNo || "",
      car_id: car?.data?.carId || "",
      country_id: 1,
      region_id: 1,
      price: car?.data?.fobPrice || 0,
      myear: car?.data?.registrationYear || "",
      ryear: car?.data?.registrationYear || "",
      rmonth: car?.data?.registrationMonth || "",
      transmission: car?.data?.transmissionName || "",
      state: state,
      status: "Available",
      engine_size: convertToNumber(car?.data?.engineSize || 0).toString(),
      drive: car?.data?.driveName || "",
      mileage: convertToNumber(car?.data?.mileage || 0).toString(),
      fuel_type: car?.data?.fuelName || "",
      seats: car?.data?.seats.toString() || "0",
      doors: car?.data?.doors.toString() || "0",
      ex_color: car?.data?.exteriorColor || "",
      in_color: car?.data?.interiorColor || "",
      lot_no: car?.data?.lotNo || "",
      main_image: extractJanUrl(car?.data?.carImages[0]?.imagePath),
      steering: car?.data?.steeringName || "",
      seller_id: 1,
      supplier_id: 1,
      source_url:
        `https://janjapan.com/used-car/tanzania-10/${car?.data?.makerName}-${car?.data?.makeId}/${car?.data?.modelName}-${car?.data?.makeId}/${car?.data?.carId}`.toLowerCase(),
      vimages,
    };

    try {
      await mutate({ data });
    } catch (error) {
      console.error("Error during mutation:", error);
    }
  };

  return (
    <Modal
      title="Sync Vehicle with Veko"
      open={isVisible}
      onCancel={() => setVisible(false)}
      footer={null}
    >
      {fetching ? (
        <div className="">Fetching ....</div>
      ) : (
        <div className="">
          <div className="py-4">
            <div className="flex text-lg">
              <div className="font-bold uppercase">
                {car?.data?.makerName} {car?.data?.modelName}
              </div>
              <div
                className="flex items-center px-2"
                style={{ color: `${statusColors[car?.data?.cityName]}` }}
              >
                [ {car?.data?.cityName} ]
              </div>
            </div>
            <div className="">CHASSIS: {car?.data?.chassisNo}</div>
            <div className="">
              {car?.data?.makerName} {car?.data?.modelName}
            </div>
          </div>

          {car?.data?.carImages?.length > 0 && (
            <img
              src={
                extractJanUrl(car?.data?.carImages[0]?.imagePath) || placeholder
              }
              alt=""
              width={"1080"}
            />
          )}

          <div className="mb-4">
            <div className="flex justify-between items-center border-b border-t bg-gray-100 px-2 py-3">
              <div className="">
                <span className="font-bold">VEKO</span>
                <span className="font-thin">AFRICA</span>
              </div>
            </div>
            <div className="flex justify-between p-2 text-xs">
              <div className="">Sync with Veko App</div>
              <button
                onClick={() => onSubmit()}
                className="flex items-center border px-2 py-1 text-green-600 bg-green-50"
              >
                <GoSync className="mr-1" /> {isLoading ? "Syncing..." : "Sync"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
